// src/components/ProblemViewComponents/SubtasksSection.tsx
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Collapse,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import { Subtask, SubtaskStatus } from '../../../models/Subtask';
import { useSubtasksProcessed } from '../../../hooks/useSubtasksProcessed';

interface SubtasksSectionProps {
  subtasks: Subtask[];
  loading: boolean;
  problemId: string;
  userId: string;
  onStatusChange: (subtaskId: string, status: SubtaskStatus) => Promise<void>;
}

const SubtasksSection: React.FC<SubtasksSectionProps> = ({
  subtasks,
  loading,
  problemId,
  userId,
  onStatusChange,
}) => {
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const { processedRollen, loading: processedLoading } = useSubtasksProcessed(problemId, userId);

  if (subtasks.length === 0) {
    return null;
  }

  const getProcessedContent = (subtaskId: string) => {
    return processedRollen.find(p => p.subtaskId === subtaskId)?.processedContent;
  };

  return (
    <Card variant="outlined" style={{ marginTop: '16px' }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Subtasks
          {(loading || processedLoading) && (
            <CircularProgress size={20} style={{ marginLeft: '10px' }} />
          )}
        </Typography>

        {subtasks.map((subtask, index) => {
          const processedContent = getProcessedContent(subtask.id);
          const isCompleted = subtask.status === 'completed';
          const isProcessing = subtask.status === 'in_progress';

          return (
            <React.Fragment key={subtask.id}>
              <Box
                sx={{
                  py: 1.5,
                  px: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  '&:hover': {
                    bgcolor: 'rgba(0, 0, 0, 0.02)',
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                  }}
                  onClick={() => setExpandedId(expandedId === subtask.id ? null : subtask.id)}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    {isCompleted ? (
                      <CheckCircleIcon color="success" />
                    ) : isProcessing ? (
                      <AutorenewIcon className="rotating" color="primary" />
                    ) : null}
                    <Typography variant="body1">{subtask.aufgabe}</Typography>
                  </Box>
                  <IconButton size="small">
                    {expandedId === subtask.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                <Collapse in={expandedId === subtask.id}>
                  <Box sx={{ mt: 2, pl: 4 }}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      {subtask.aufgabebeschreibung}
                    </Typography>

                    {processedContent && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 2,
                          bgcolor: 'grey.50',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'grey.200',
                        }}
                      >
                        <Typography variant="subtitle2" color="primary" gutterBottom>
                          Result:
                        </Typography>
                        <ReactMarkdown rehypePlugins={[rehypeRaw, rehypeSanitize]}>
                          {processedContent}
                        </ReactMarkdown>
                      </Box>
                    )}
                  </Box>
                </Collapse>
              </Box>
              {index < subtasks.length - 1 && <Divider />}
            </React.Fragment>
          );
        })}
      </CardContent>
    </Card>
  );
};

export default SubtasksSection;
